/**
 * UTILIDADES GEOMÉTRICAS
 * Algoritmos para detección de puntos en polígonos y formas geométricas
 */

import type { Point2D, ClickableArea, RelativeCoordinates } from '@types';

/**
 * Detecta si un punto está dentro de un polígono usando Ray Casting
 */
export function isPointInPolygon(x: number, y: number, corners: Point2D[]): boolean {
  if (!corners || corners.length < 3) {
    return false;
  }
  
  let inside = false;
  
  // Algoritmo Ray Casting
  for (let i = 0, j = corners.length - 1; i < corners.length; j = i++) {
    const cornerI = corners[i];
    const cornerJ = corners[j];
    
    if (!cornerI || !cornerJ) continue;
    
    const xi = cornerI.x;
    const yi = cornerI.y;
    const xj = cornerJ.x;
    const yj = cornerJ.y;
    
    // Verificar si el rayo horizontal cruza esta arista
    if (((yi > y) !== (yj > y)) && 
        (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  
  return inside;
}

/**
 * Detecta si un punto está dentro de un rectángulo
 */
export function isPointInRectangle(x: number, y: number, rect: ClickableArea): boolean {
  if (
    rect.x1 === undefined ||
    rect.x2 === undefined ||
    rect.y1 === undefined ||
    rect.y2 === undefined
  ) {
    return false;
  }
  return x >= rect.x1 && x <= rect.x2 &&
         y >= rect.y1 && y <= rect.y2;
}

/**
 * Detecta en qué área clickeable está un punto
 */
export function detectClickedArea(
  x: number, 
  y: number, 
  areas: ClickableArea[]
): (ClickableArea & { detectionMethod: 'polygon' | 'rectangle' }) | null {
  // Ordenar áreas por tamaño (más pequeñas primero) para dar prioridad a las más específicas
  const sortedAreas = [...areas].sort((a, b) => {
    // Verificar que las coordenadas existen antes de calcular el área
    if (a.x1 === undefined || a.x2 === undefined || a.y1 === undefined || a.y2 === undefined ||
        b.x1 === undefined || b.x2 === undefined || b.y1 === undefined || b.y2 === undefined) {
      return 0; // Si faltan coordenadas, no cambiar el orden
    }
    
    const areaA = (a.x2 - a.x1) * (a.y2 - a.y1);
    const areaB = (b.x2 - b.x1) * (b.y2 - b.y1);
    return areaA - areaB;
  });

  for (const area of sortedAreas) {
    // Priorizar detección de polígono rotado
    if (area.corners && area.corners.length === 4) {
      if (isPointInPolygon(x, y, area.corners)) {
        return {
          ...area,
          detectionMethod: 'polygon'
        };
      }
    } 
    // Fallback a detección rectangular
    else if (isPointInRectangle(x, y, area)) {
      return {
        ...area,
        detectionMethod: 'rectangle'
      };
    }
  }
  
  return null;
}

/**
 * Calcula la distancia entre dos puntos
 */
export function calculateDistance(point1: Point2D, point2: Point2D): number {
  const dx = point2.x - point1.x;
  const dy = point2.y - point1.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Calcula el centro de un polígono
 */
export function calculatePolygonCenter(corners: Point2D[]): Point2D {
  if (!corners || corners.length === 0) {
    return { x: 0, y: 0 };
  }
  
  const sum = corners.reduce((acc, corner) => {
    if (!corner) return acc;
    return {
      x: acc.x + corner.x,
      y: acc.y + corner.y
    };
  }, { x: 0, y: 0 });
  
  return {
    x: sum.x / corners.length,
    y: sum.y / corners.length
  };
}

/**
 * Calcula el bounding box de un conjunto de puntos
 */
export function calculateBoundingBox(points: Point2D[]): ClickableArea {
  if (!points || points.length === 0) {
    return { id: '', x1: 0, y1: 0, x2: 0, y2: 0 };
  }
  
  const validPoints = points.filter(p => p && typeof p.x === 'number' && typeof p.y === 'number');
  if (validPoints.length === 0) {
    return { id: '', x1: 0, y1: 0, x2: 0, y2: 0 };
  }
  
  const xs = validPoints.map(p => p.x);
  const ys = validPoints.map(p => p.y);
  
  return {
    id: '',
    x1: Math.min(...xs),
    y1: Math.min(...ys),
    x2: Math.max(...xs),
    y2: Math.max(...ys)
  };
}

/**
 * Rota un punto alrededor de un centro
 */
export function rotatePoint(point: Point2D, center: Point2D, angle: number): Point2D {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  
  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos
  };
}

/**
 * Valida que las coordenadas estén en rango válido
 */
export function validateCoordinates(x: number, y: number): boolean {
  return typeof x === 'number' && typeof y === 'number' &&
         x >= 0 && x <= 1 && y >= 0 && y <= 1 &&
         !isNaN(x) && !isNaN(y);
}

/**
 * Calcula el área de un polígono
 */
export function calculatePolygonArea(corners: Point2D[]): number {
  if (!corners || corners.length < 3) {
    return 0;
  }
  
  let area = 0;
  const n = corners.length;
  
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const cornerI = corners[i];
    const cornerJ = corners[j];
    
    if (!cornerI || !cornerJ) continue;
    
    area += cornerI.x * cornerJ.y;
    area -= cornerJ.x * cornerI.y;
  }
  
  return Math.abs(area) / 2;
}

/**
 * Calcula el perímetro de un polígono
 */
export function calculatePolygonPerimeter(corners: Point2D[]): number {
  if (!corners || corners.length < 2) {
    return 0;
  }
  
  let perimeter = 0;
  
  for (let i = 0; i < corners.length; i++) {
    const j = (i + 1) % corners.length;
    const cornerI = corners[i];
    const cornerJ = corners[j];
    
    if (!cornerI || !cornerJ) continue;
    
    perimeter += calculateDistance(cornerI, cornerJ);
  }
  
  return perimeter;
}

/**
 * Encuentra el punto más cercano en un polígono
 */
export function findClosestPointOnPolygon(point: Point2D, corners: Point2D[]): Point2D {
  if (!corners || corners.length === 0) {
    return point;
  }
  
  const firstCorner = corners[0];
  if (!firstCorner) {
    return point;
  }
  
  let closestPoint = firstCorner;
  let minDistance = calculateDistance(point, firstCorner);
  
  // Verificar vértices
  for (const corner of corners) {
    if (!corner) continue;
    
    const distance = calculateDistance(point, corner);
    if (distance < minDistance) {
      minDistance = distance;
      closestPoint = corner;
    }
  }
  
  // Verificar aristas
  for (let i = 0; i < corners.length; i++) {
    const j = (i + 1) % corners.length;
    const cornerI = corners[i];
    const cornerJ = corners[j];
    
    if (!cornerI || !cornerJ) continue;
    
    const edgePoint = findClosestPointOnLineSegment(point, cornerI, cornerJ);
    const distance = calculateDistance(point, edgePoint);
    
    if (distance < minDistance) {
      minDistance = distance;
      closestPoint = edgePoint;
    }
  }
  
  return closestPoint;
}

/**
 * Encuentra el punto más cercano en un segmento de línea
 */
export function findClosestPointOnLineSegment(
  point: Point2D, 
  lineStart: Point2D, 
  lineEnd: Point2D
): Point2D {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  
  if (dx === 0 && dy === 0) {
    return lineStart;
  }
  
  const t = Math.max(0, Math.min(1, 
    ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / (dx * dx + dy * dy)
  ));
  
  return {
    x: lineStart.x + t * dx,
    y: lineStart.y + t * dy
  };
}

/**
 * Verifica si dos rectángulos se intersectan
 */
export function rectanglesIntersect(rect1: ClickableArea, rect2: ClickableArea): boolean {
  if (
    rect1.x1 === undefined || rect1.x2 === undefined ||
    rect1.y1 === undefined || rect1.y2 === undefined ||
    rect2.x1 === undefined || rect2.x2 === undefined ||
    rect2.y1 === undefined || rect2.y2 === undefined
  ) {
    return false;
  }
  return !(rect1.x2 < rect2.x1 || 
           rect2.x2 < rect1.x1 || 
           rect1.y2 < rect2.y1 || 
           rect2.y2 < rect1.y1);
}

/**
 * Normaliza coordenadas a rango [0, 1]
 */
export function normalizeCoordinates(
  x: number, 
  y: number, 
  width: number, 
  height: number
): RelativeCoordinates {
  return {
    x: Math.max(0, Math.min(1, x / width)),
    y: Math.max(0, Math.min(1, y / height))
  };
}